/**
 * 登录页面
 *
 * 布局参考：model-evaluation-platform-frontend/src/app/auth/layout.tsx
 * 交互规范：
 * - 已有 accessToken → 直接跳到首页（或 ?redirect= 指定页面）
 * - 提交前用 RSA-OAEP 加密密码，降级为 plain: 前缀
 * - 登录成功 → 跳转到安全归一化后的 redirect，或首页
 * - redirect 安全归一化：只允许站内相对路径，拒绝 http(s)://、//、/auth/*
 */

import { createEffect, createSignal, For, Show } from "solid-js"
import { isAuthenticated, login, normalizeRedirect, withBasePath } from "@/utils/zd-auth"
import loginBgImage from "@/assets/self-develop/loginBg.png"
import leftTitleImage from "@/assets/self-develop/leftTitle.png"
import rightTitleImage from "@/assets/self-develop/rightTitle.png"
import userIconImage from "@/assets/self-develop/user.png"
import passwordIconImage from "@/assets/self-develop/password.png"

// ─── 图标（内联 SVG） ────────────────────────────────────────────────────────

function MessageSquareIcon(props: { class?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      class={props.class} aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function CodeIcon(props: { class?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      class={props.class} aria-hidden="true">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  )
}

function GitBranchIcon(props: { class?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      class={props.class} aria-hidden="true">
      <line x1="6" x2="6" y1="3" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
  )
}

function HistoryIcon(props: { class?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      class={props.class} aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </svg>
  )
}

function EyeIcon(props: { class?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      class={props.class} aria-hidden="true">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function EyeOffIcon(props: { class?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      class={props.class} aria-hidden="true">
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  )
}

function SpinnerIcon(props: { class?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      class={props.class} aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

// ─── 特性卡片数据 ────────────────────────────────────────────────────────────

type FeatureIcon = (props: { class?: string }) => ReturnType<typeof CodeIcon>

const FEATURES: Array<{ Icon: FeatureIcon; title: string; desc: string }> = [
  { Icon: MessageSquareIcon, title: "智能安全问答", desc: "多轮对话解答攻防与合规难题" },
  { Icon: CodeIcon, title: "代码风险洞察", desc: "自动识别缺陷漏洞与安全隐患" },
  { Icon: GitBranchIcon, title: "沙箱隔离协作", desc: "多项目工作区独立并行开发" },
  { Icon: HistoryIcon, title: "会话全程留痕", desc: "任务进度与结果随时可回溯" },
]

// ─── 工具函数 ─────────────────────────────────────────────────────────────────

function getRedirectTarget(): string {
  return normalizeRedirect(new URLSearchParams(location.search).get("redirect"))
}

// ─── 登录页面 ─────────────────────────────────────────────────────────────────

export function LoginPage() {
  const [account, setAccount] = createSignal("")
  const [password, setPassword] = createSignal("")
  const [showPassword, setShowPassword] = createSignal(false)
  const [loading, setLoading] = createSignal(false)
  const [error, setError] = createSignal("")

  createEffect(() => {
    if (isAuthenticated()) {
      window.location.href = withBasePath(getRedirectTarget())
    }
  })

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault()
    setError("")
    if (!account().trim() || !password().trim()) {
      setError("请输入账号和密码")
      return
    }
    setLoading(true)
    try {
      await login(account(), password())
      window.location.href = withBasePath(getRedirectTarget())
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败，请稍后重试")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      class="flex min-h-dvh w-full select-none [&_input]:select-text"
      style={{ background: "#f8fafc" }}
    >

      {/* ══════════════════════════════════════════════════════
          左侧：品牌 + 特性展示（lg 及以上可见）
      ══════════════════════════════════════════════════════ */}
      <aside
        class="relative hidden overflow-hidden lg:flex lg:w-[58%]"
      >
        {/* 深蓝渐变底色（背景图加载前的兜底） */}
        {/* <div class="absolute inset-0" style={{
          background: "linear-gradient(135deg, #0b1220 0%, #10213f 45%, #123162 100%)",
        }} /> */}

        {/* 背景图（AI 科技风，100% 100% 完整自适应铺满左侧区域） */}
        <div
          class="absolute inset-0"
          style={{
            "background-color": "#050b18",
            "background-image": `url(${loginBgImage})`,
            "background-size": "100% 100%",
            "background-position": "center",
            "background-repeat": "no-repeat",
          }}
        />

        {/* 网格纹理（28px，slate-200 线条） */}
        {/* <div
          class="absolute inset-0"
          style={{
            opacity: 0.07,
            "background-size": "28px 28px",
            "background-image":
              "linear-gradient(to right, #cbd5e1 1px, transparent 1px)," +
              "linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)",
          }}
        /> */}

        {/* 扫描线动效 */}
        <div class="auth-scan-line auth-scan-line--down" />

        {/* 双色辉光装饰 */}
        {/* <div
          class="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 18% 20%, rgba(59,130,246,0.30), transparent 42%)," +
              "radial-gradient(circle at 82% 26%, rgba(6,182,212,0.25), transparent 38%)",
          }}
        /> */}

        {/* 内容区（与参考一致：max-w-[920px] 水平居中列） */}
        <div class="relative z-10 flex h-full w-full flex-col px-14 py-16">
          <div class="mx-auto w-full max-w-[920px]">
            {/* 品牌标题（logo 与浏览器标签页 favicon 一致；样式 1:1 复刻参考 auth/layout.tsx） */}
            <div class="mt-4 items-center gap-4 rounded-xl bg-transparent px-0 py-3">
              {/* <img
                src="/favicon-zdas-tile.png"
                alt=""
                aria-hidden="true"
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 object-contain"
              /> */}
              {/* 标题图 183×36，距页面左边缘 62px、上边缘 44px */}
              <div class="absolute" style={{ top: "44px", left: "62px" }}>
                <img src={leftTitleImage} alt="" />
              </div>
              <div class="absolute" style={{ top: "94px", left: "62px" }}>
                <p
                  class="whitespace-nowrap"
                  style={{
                    "font-family": "Microsoft YaHei, sans-serif",
                    "font-weight": 400,
                    "font-size": "20px",
                    color: "#DCDCDC",
                    "line-height": "28px",
                  }}
                >
                  智能安全问答 / 代码风险洞察 / 沙箱隔离协作 / 会话全程留痕
                </p>
              </div>
            </div>
          </div>

          <div class="mx-auto mt-[179px] w-full max-w-[920px]">
            {/* 特性卡片 2×2 */}
            {/* <div class="grid grid-cols-2 gap-4">
              <For each={FEATURES}>
                {(feat) => (
                  <div class="h-[96px] rounded-xl border border-white/15 bg-white/[0.07] px-4 py-3 backdrop-blur-sm">
                    <div class="flex h-full items-center gap-3">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/5">
                        <feat.Icon class="h-5 w-5 text-cyan-300" />
                      </div>
                      <div class="flex h-10 min-w-0 flex-1 flex-col justify-between">
                        <p class="text-lg leading-[1.05] font-bold text-white">{feat.title}</p>
                        <p class="text-sm leading-[1.1] text-slate-300">{feat.desc}</p>
                      </div>
                    </div>
                  </div>
                )}
              </For>
            </div> */}
          </div>
        </div>
      </aside>

      {/* ══════════════════════════════════════════════════════
          右侧：登录表单
      ══════════════════════════════════════════════════════ */}
      <main class="flex w-full flex-col lg:w-[42%]">

        {/* 移动端顶部品牌条（lg 隐藏） */}
        {/* <div class="flex items-center gap-2.5 px-6 py-4 lg:hidden" style={{ background: "#0f172a" }}>
          <img
            src="/favicon-zdas-tile.png"
            alt=""
            aria-hidden="true"
            class="h-8 w-8 shrink-0 rounded-lg object-contain"
          />
          <span class="text-base font-bold text-white">智盾 · AI安全助手</span>
        </div> */}

        {/* 表单居中区（水平居中；垂直方向距顶部 140px，居中偏上） */}
        <div
          class="flex flex-1 items-start justify-center px-4 pt-[180px] pb-8 sm:px-8"
          style={{ background: "#f8fafc" }}
        >
          <div class="w-full max-w-sm">

            {/* 标题 */}
            <div class="mb-8">
              <img src={rightTitleImage} alt="" style={{ "margin-bottom": "80px" }} class="mb-4" />
              <h2
                class="text-2xl font-bold tracking-tight"
                style={{ color: "#0f172a", "font-family": "Source Han Sans CN, Source Han Sans CN" }}
              >
                登录
              </h2>
              <p class="mt-2 text-sm" style={{ color: "#64748b" }}>
                登录您的账号以继续使用平台
              </p>
            </div>

            <form class="space-y-5" onSubmit={handleSubmit} noValidate>

              {/* 账号 */}
              <div class="space-y-2">
                <label
                  for="zd-account"
                  class="block text-sm font-medium"
                  style={{ color: "#3b3b3b", "font-weight": "bold", "font-size": "16px" }}
                >
                  账号
                </label>
                <div class="relative">
                  <img src={userIconImage} alt="" aria-hidden="true" class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none" />
                  <input
                    id="zd-account"
                    type="text"
                    value={account()}
                    onInput={(e) => setAccount(e.currentTarget.value)}
                    placeholder="用户名 / 邮箱"
                    autocomplete="username"
                    disabled={loading()}
                    class="w-full h-11 rounded-lg pl-10 pr-3 text-sm outline-none transition disabled:opacity-50"
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      color: "#0f172a",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "#3b82f6"
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59,130,246,0.15)"
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "#e2e8f0"
                      e.currentTarget.style.boxShadow = "none"
                    }}
                  />
                </div>
              </div>

              {/* 密码 */}
              <div class="space-y-2">
                <label
                  for="zd-password"
                  class="block text-sm font-medium"
                  style={{ color: "#3b3b3b", "font-weight": "bold", "font-size": "16px" }}
                >
                  密码
                </label>
                <div class="relative">
                  <img src={passwordIconImage} alt="" aria-hidden="true" class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none" />
                  <input
                    id="zd-password"
                    type={showPassword() ? "text" : "password"}
                    value={password()}
                    onInput={(e) => setPassword(e.currentTarget.value)}
                    placeholder="请输入密码"
                    autocomplete="current-password"
                    disabled={loading()}
                    class="w-full h-11 rounded-lg pl-10 pr-10 text-sm outline-none transition disabled:opacity-50"
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      color: "#0f172a",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "#3b82f6"
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59,130,246,0.15)"
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "#e2e8f0"
                      e.currentTarget.style.boxShadow = "none"
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    class="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: "#94a3b8" }}
                    aria-label={showPassword() ? "隐藏密码" : "显示密码"}
                    tabIndex={-1}
                  >
                    <Show when={showPassword()} fallback={<EyeIcon class="h-4 w-4" />}>
                      <EyeOffIcon class="h-4 w-4" />
                    </Show>
                  </button>
                </div>
              </div>

              {/* 错误提示 */}
              <Show when={error()}>
                {(msg) => (
                  <div
                    role="alert"
                    class="flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs"
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      color: "#dc2626",
                    }}
                  >
                    <span class="shrink-0 mt-px">⚠</span>
                    <span>{msg()}</span>
                  </div>
                )}
              </Show>

              {/* 登录按钮 */}
              <button
                type="submit"
                disabled={loading()}
                aria-busy={loading()}
                class="w-full rounded-lg text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ height: "50px", background: loading() ? "#1e293b" : "#0f172a" }}
                onMouseEnter={(e) => { if (!loading()) e.currentTarget.style.background = "#1e293b" }}
                onMouseLeave={(e) => { if (!loading()) e.currentTarget.style.background = "#0f172a" }}
              >
                <Show when={loading()} fallback="登录">
                  <SpinnerIcon class="h-4 w-4 animate-spin" />
                  <span>登录中...</span>
                </Show>
              </button>

            </form>

            {/* 版权 */}
            {/* <p class="mt-8 text-center text-xs" style={{ color: "#94a3b8" }}>
              © 智盾安全
            </p> */}
          </div>
        </div>
      </main >

    </div >
  )
}
